import styled from 'styled-components'

export const HeaderContainer = styled.div`
  /* The outer ZoneCard <Card> owns the border + radius; this container now
     holds the whole card body (banner + segmented panes), so it stays flat. */
  background: ${({ theme }) => theme.colors.background.primary};
  overflow: hidden;
`

export const BannerSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.medium};
  padding: ${({ theme }) => theme.spacing.medium};
  background: ${({ theme }) => theme.colors.background.secondary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  flex-wrap: wrap;

  @media (max-width: 640px) {
    padding: ${({ theme }) => theme.spacing.small};
    gap: ${({ theme }) => theme.spacing.small};
  }

  @media (min-width: 1200px) {
    padding: ${({ theme }) => theme.spacing.large};
  }
`

export const BannerLeft = styled.div`
  flex: 1;
  min-width: 0;
  word-wrap: break-word;
  overflow-wrap: break-word;
`

export const BannerRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing.xsmall};
  flex-shrink: 0;
`

export const PlanTypeTitle = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.section};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.3;
`

export const ZoneName = styled.div`
  margin-top: ${({ theme }) => theme.spacing.xsmall};
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.4;
`

export const ZoneNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  flex-wrap: wrap;
`

type CoverageVariant = 'high' | 'medium' | 'low'

const coverageColors: Record<
  CoverageVariant,
  { bg: string; border: string; fg: string; dot: string }
> = {
  high: {
    bg: '#dcfce7',
    border: '#bbf7d0',
    fg: '#15803d',
    dot: '#22c55e',
  },
  medium: {
    bg: '#fef3c7',
    border: '#fde68a',
    fg: '#b45309',
    dot: '#f59e0b',
  },
  low: {
    bg: '#f1f5f9',
    border: '#e2e8f0',
    fg: '#64748b',
    dot: '#94a3b8',
  },
}

export const CoveragePill = styled.span<{ $variant: CoverageVariant }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 7px;
  border-radius: 999px;
  font-size: ${({ theme }) => theme.typography.fontSize.micro};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  line-height: 1.5;
  font-variant-numeric: tabular-nums;
  background: ${({ $variant }) => coverageColors[$variant].bg};
  border: 1px solid ${({ $variant }) => coverageColors[$variant].border};
  color: ${({ $variant }) => coverageColors[$variant].fg};
`

export const CoverageDot = styled.span<{ $variant: CoverageVariant }>`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: ${({ $variant }) => coverageColors[$variant].dot};
  flex-shrink: 0;
`

export const CoverageSuffix = styled.span`
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  opacity: 0.8;
`

export const BadgesRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.small};
  flex-wrap: wrap;
`

export const AdoptionDate = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.tertiary};
`

export const ContentArea = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.medium};
  padding: ${({ theme }) => theme.spacing.medium};

  @media (max-width: 640px) {
    padding: ${({ theme }) => theme.spacing.small};
  }

  @media (min-width: 1200px) {
    padding: ${({ theme }) => theme.spacing.large};
    gap: ${({ theme }) => theme.spacing.large};
  }
`

/*
 * Flex-wrap basis values drive the side-by-side vs stacked layout
 * based on the *container's* width rather than the viewport's. The
 * two columns sit side by side when the ContentArea can fit ~740px+,
 * and stack as soon as it's narrower — which correctly handles the
 * case where the map panel steals a large chunk of the viewport.
 */
export const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.small};
  flex: 1 1 420px;
  min-width: 0;
`

export const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 320px;
  min-width: 0;
`

export const RegulationCard = styled.div<{ $hasReference: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  background: ${({ theme }) => theme.colors.background.secondary};
  border: 1px solid transparent;
  border-radius: ${({ theme }) => theme.borderRadius.default};
  padding: ${({ theme }) => `${theme.spacing.small} ${theme.spacing.default}`};
  cursor: ${({ $hasReference }) => ($hasReference ? 'pointer' : 'default')};
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover {
    ${({ $hasReference, theme }) =>
      $hasReference
        ? `background: ${theme.colors.primary}0d;
           border-color: ${theme.colors.primary};`
        : ''}
  }
`

export const RegulationBody = styled.div`
  flex: 1;
  min-width: 0;
`

export const RegulationChevron = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text.tertiary};
  flex-shrink: 0;
  transition: ${({ theme }) => theme.transitions.fast};

  ${RegulationCard}:hover & {
    color: ${({ theme }) => theme.colors.primary};
    transform: translateX(2px);
  }
`

export const RegulationLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.micro};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.tertiary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: ${({ theme }) => theme.spacing.xsmall};
`

export const RegulationValue = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  color: ${({ theme }) => theme.colors.text.primary};
`

export const TabBar = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.medium};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  margin-bottom: ${({ theme }) => theme.spacing.small};
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 640px) {
    gap: ${({ theme }) => theme.spacing.small};
  }
`

export const Tab = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  background: none;
  border: none;
  border-bottom: 2px solid
    ${({ $active, theme }) =>
      $active ? theme.colors.text.primary : 'transparent'};
  padding: ${({ theme }) => `${theme.spacing.small} 0`};
  cursor: pointer;
  white-space: nowrap;
  min-height: 36px;
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ $active, theme }) =>
    $active
      ? theme.typography.fontWeight.semibold
      : theme.typography.fontWeight.normal};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.text.primary : theme.colors.text.tertiary};
  transition: color ${({ theme }) => theme.transitions.fast};

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`

export const TabCount = styled.span<{ $active: boolean }>`
  font-size: ${({ theme }) => theme.typography.fontSize.micro};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  padding: 1px 6px;
  border-radius: ${({ theme }) => theme.borderRadius.round};
  background: ${({ $active, theme }) =>
    $active ? '#fef3c7' : theme.colors.primaryLight};
  color: ${({ $active, theme }) =>
    $active ? '#92400e' : theme.colors.primary};
`

export const TabContent = styled.div``

export const QAButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.default};
  background: ${({ theme }) => theme.colors.primaryLight};
  color: ${({ theme }) => theme.colors.primary};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.default};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  white-space: nowrap;
  min-width: 0;
  max-width: 100%;
  text-overflow: ellipsis;
  overflow: hidden;

  @media (min-width: 768px) {
    font-size: ${({ theme }) => theme.typography.fontSize.body};
  }

  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.text.inverse};
  }

  svg {
    flex-shrink: 0;
  }
`

// ---------------------------------------------------------------------------
// Segmented card: a top toolbar switches between "Development potential"
// (the metrics table), "Purpose & rules" and "Documents". The Q&A button
// lives in the toolbar so it stays visible on every segment.
// ---------------------------------------------------------------------------

export const SegmentBar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => theme.spacing.medium};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};

  @media (max-width: 640px) {
    padding: ${({ theme }) => theme.spacing.small};
  }
`

export const SegmentGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.small};
  flex: 1 1 auto;
  min-width: 0;
`

export const Segment = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => `${theme.spacing.small} ${theme.spacing.default}`};
  border: 1px solid
    ${({ $active, theme }) =>
      $active ? theme.colors.primary : theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.round};
  background: ${({ $active, theme }) =>
    $active ? theme.colors.primary : theme.colors.background.primary};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.text.inverse : theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ $active, theme }) =>
    $active
      ? theme.typography.fontWeight.semibold
      : theme.typography.fontWeight.medium};
  cursor: pointer;
  white-space: nowrap;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ $active, theme }) =>
      $active ? theme.colors.text.inverse : theme.colors.primary};
  }
`

export const SegmentCount = styled.span<{ $active: boolean }>`
  font-size: ${({ theme }) => theme.typography.fontSize.micro};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  padding: 1px 6px;
  border-radius: ${({ theme }) => theme.borderRadius.round};
  background: ${({ $active, theme }) =>
    $active ? 'rgba(255, 255, 255, 0.25)' : theme.colors.primaryLight};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.text.inverse : theme.colors.primary};
`

/** Wrapper that shows/hides a pane without unmounting it (keeps doc-count
 *  fetched + metric edit state alive across segment switches). */
export const PaneWrap = styled.div<{ $active: boolean }>`
  display: ${({ $active }) => ($active ? 'block' : 'none')};
  margin-bottom: ${({ theme }) => theme.spacing.large};
`

/** Padded pane body for the "Documents" segment. */
export const Pane = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => theme.spacing.medium};

  @media (max-width: 640px) {
    padding: ${({ theme }) => theme.spacing.small};
  }
`

/** The metrics table brings its own horizontal + bottom padding, so the
 *  potential pane only needs a little breathing room under the toolbar. */
export const PotentialPane = styled.div`
  padding-top: ${({ theme }) => theme.spacing.medium};
`

/** Purpose / arrangement / references stacked below the metrics table inside
 *  the "Development potential" pane. Horizontal padding matches the metrics
 *  table (large) so the cards align with the table edges. */
export const TrailingContext = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.small};
  padding: 0 ${({ theme }) => theme.spacing.large}
    ${({ theme }) => theme.spacing.large};

  @media (max-width: 640px) {
    padding: 0 ${({ theme }) => theme.spacing.small}
      ${({ theme }) => theme.spacing.small};
  }
`

/** Thin rule separating the metrics table from the rules/references below. */
export const SectionDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.border.light};
  margin: ${({ theme }) => theme.spacing.xsmall} 0;
`

/** "PURPOSE & RULES" / "REFERENCES" mini section header with a trailing rule.
 *  `$spaced` adds extra top margin to separate one group from the previous. */
export const SectionLabel = styled.div<{ $spaced?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  margin-top: ${({ $spaced, theme }) =>
    $spaced ? theme.spacing.medium : theme.spacing.xsmall};
`

export const SectionLabelText = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.micro};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.tertiary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

/** Amber count chip on the References label (matches the old References tab). */
export const SectionCount = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.micro};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  padding: 1px 6px;
  border-radius: ${({ theme }) => theme.borderRadius.round};
  background: #fef3c7;
  color: #92400e;
`

export const SectionRule = styled.span`
  flex: 1;
  height: 1px;
  background: ${({ theme }) => theme.colors.border.light};
`

/** Wraps the metrics legend at the bottom of the potential pane so it aligns
 *  with the table (the legend supplies its own vertical margins + border). */
export const LegendSlot = styled.div`
  padding: 0 ${({ theme }) => theme.spacing.large};

  @media (max-width: 640px) {
    padding: 0 ${({ theme }) => theme.spacing.small};
  }
`
